import Button from "components/button";
import Pagination from "components/pagination";
import { Breadcrumbs } from "components/breadcrumbs";
import { Link } from "react-router-dom";
import { PlusIcon } from "@heroicons/react/solid";
import { useCreate, useTable } from "@refinedev/core";
import { useMemo } from "react";
import { FullScreenLoading } from "components/fullScreenLoading";
import toast from "react-hot-toast";
import { Filter } from "./components/filter";
import { IFipixEventItem } from "interfaces";
import { EventCard } from "./components";
import { isHaveActionPermission } from "utills/helpers";

const DEFAULT_PAGE_SIZE = 9;

export const FipixEventList: React.FC = () => {
  const { mutate, isLoading: isLoading } = useCreate();

  const {
    tableQuery,
    filters,
    setFilters,
  } = useTable<IFipixEventItem>({
    resource: "fipix/events",
    syncWithLocation: true,
    pagination: {
      mode: "off",
      pageSize: DEFAULT_PAGE_SIZE
    },
    meta: {
      resource: "data",
    },
  });
  
  const currentFilterValues = useMemo(() => {
    const logicalFilters = filters.flatMap((item) =>
      "field" in item ? item : []
    );

    return {
      q: logicalFilters.find((item) => item.field === "q")?.value || "",
      is_active: logicalFilters.find((item) => item.field === "is_active")?.value || "",
      hidden: logicalFilters.find((item) => item.field === "hidden")?.value || "",
      organization_id: logicalFilters.find((item) => item.field === "organization_id")?.value || "",
    };
  }, [filters]);

  const _toggleActive = (id: string, action: string) => {
    if (!isHaveActionPermission("events", "fipix", "edit")) return;
    const actionText = action === "hidden" ? "hide" : action === "display" ? "show" : action;
    if (window.confirm(`Are you sure you want to ${actionText} this event on the Fipix homepage?`)) {
      mutate({
        resource: `fipix/events/${id}/actions/${action}`,
        meta: {
          ignoreResourceWrapper: true
        },
        values: {},
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
          tableQuery.refetch();
        },
      });
    }
  };

  const _handlePageChanged = (page: number) => {
    const updatedFilters = [...filters].flatMap(item => "field" in item ? item: []);
    const pageIndex = updatedFilters.findIndex(filter => filter.field === "page");
    if (pageIndex < 0) {
      updatedFilters.push({field: "page", operator: "eq", value: page})
    } else {
      updatedFilters[pageIndex].value = page;
    }

    setFilters(updatedFilters);
  }

  if (tableQuery.isLoading) {
    return <FullScreenLoading />;
  }

  return (
    <div className="wrapper">
      <Breadcrumbs
        {...(isHaveActionPermission("events", "fipix", "create") && {
          additionalLabel: (
            <Link to={"/fipix/events/create"} target="_blank">
              <Button>
                <span className="sr-only">Create event</span>
                <PlusIcon className="w-4 h-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">Create Event</span>
              </Button>
            </Link>
          ),
        })}
      />
      <div className="box-container">
        <Filter
          currentFilterValues={currentFilterValues}
          setFilters={setFilters}
        />
      </div>
      <div className="box-container">
        <EventCard
          data={tableQuery?.data?.data}
          pageSize={tableQuery?.data?.pageSize}
          currentPage={tableQuery.data?.paging.current_page}
          _toggleActive={_toggleActive}
        />
        <div className="mt-6">
          <Pagination
            currentPage={tableQuery?.data?.paging.current_page}
            totalPage={tableQuery?.data?.paging.total_page}
            total={{
              totalItems: tableQuery?.data?.paging.total_item || 0,
              total: tableQuery?.data?.paging.totals || 0,
            }}
            onChange={_handlePageChanged}
          />
        </div>
      </div>
      {(tableQuery.isFetching && tableQuery.isPreviousData) || isLoading ? (
        <FullScreenLoading />
      ) : (
        <></>
      )}
    </div>
  );
};
